import { Vector2 } from './types';

class Explosion {
  position: Vector2;
  particles: Particle[] = [];
  alive: boolean = true;
  timer: number = 0;
  maxTime: number = 1.0;

  constructor(x: number, y: number) {
    this.position = { x, y };
    
    for (let i = 0; i < 20; i++) {
      const angle = (Math.PI * 2 * i) / 20;
      const speed = 50 + Math.random() * 100;
      this.particles.push(new Particle(
        x, y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed
      ));
    }
  }

  update(deltaTime: number): void {
    this.timer += deltaTime;
    if (this.timer >= this.maxTime) {
      this.alive = false;
      return;
    }

    this.particles.forEach(particle => particle.update(deltaTime));
  }

  render(ctx: CanvasRenderingContext2D): void {
    const alpha = 1 - (this.timer / this.maxTime);
    
    this.particles.forEach(particle => {
      ctx.save();
      ctx.globalAlpha = alpha;
      particle.render(ctx);
      ctx.restore();
    });
  }
}

class Particle {
  position: Vector2;
  velocity: Vector2;
  color: string;
  size: number;

  constructor(x: number, y: number, vx: number, vy: number) {
    this.position = { x, y };
    this.velocity = { x: vx, y: vy };
    this.color = this.getRandomColor();
    this.size = 2 + Math.random() * 3;
  }

  private getRandomColor(): string {
    const colors = ['#ff4444', '#ff8844', '#ffff44', '#ffffff'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  update(deltaTime: number): void {
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    
    this.velocity.x *= 0.95;
    this.velocity.y *= 0.95;
    this.velocity.y += 100 * deltaTime;
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(this.position.x, this.position.y, this.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

class EnemyExplosion extends Explosion {
  constructor(x: number, y: number, enemyType: 'small' | 'medium' | 'large') {
    super(x, y);
    this.particles = [];
    
    const particleCount = enemyType === 'large' ? 40 : enemyType === 'medium' ? 25 : 15;
    const speedMultiplier = enemyType === 'large' ? 1.5 : enemyType === 'medium' ? 1.2 : 1;
    
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.2;
      const speed = (80 + Math.random() * 120) * speedMultiplier;
      this.particles.push(new Particle(
        x, y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed
      ));
    }
    
    for (let i = 0; i < particleCount / 2; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (20 + Math.random() * 40) * speedMultiplier;
      this.particles.push(new DebrisParticle(
        x, y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed
      ));
    }
  }
}

class DebrisParticle extends Particle {
  private rotation: number = 0;
  private rotationSpeed: number;
  
  constructor(x: number, y: number, vx: number, vy: number) {
    super(x, y, vx, vy);
    this.size = 3 + Math.random() * 5;
    this.rotationSpeed = (Math.random() - 0.5) * 10;
    this.color = Math.random() > 0.5 ? '#666666' : '#999999';
  }
  
  update(deltaTime: number): void {
    super.update(deltaTime);
    this.rotation += this.rotationSpeed * deltaTime;
  }
  
  render(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.position.x, this.position.y);
    ctx.rotate(this.rotation);
    ctx.fillStyle = this.color;
    ctx.fillRect(-this.size/2, -this.size/2, this.size, this.size);
    ctx.restore();
  }
}

class PlayerDeathExplosion {
  position: Vector2;
  particles: Particle[] = [];
  shockwaves: Shockwave[] = [];
  alive: boolean = true;
  timer: number = 0;
  maxTime: number = 3.0;
  phase: number = 0;

  constructor(x: number, y: number) {
    this.position = { x, y };
  }

  update(deltaTime: number): void {
    this.timer += deltaTime;
    
    if (this.phase === 0) {
      this.createFirstExplosion();
      this.phase = 1;
    }
    
    if (this.phase === 1 && this.timer > 0.3) {
      this.createSecondExplosion();
      this.phase = 2;
    }
    
    if (this.phase === 2 && this.timer > 0.6) {
      this.createFinalExplosion();
      this.phase = 3;
    }
    
    this.particles.forEach(particle => particle.update(deltaTime));
    this.shockwaves = this.shockwaves.filter(sw => sw.radius < 300);
    this.shockwaves.forEach(sw => sw.update(deltaTime));
    
    if (this.timer >= this.maxTime) {
      this.alive = false;
    }
  }
  
  private createFirstExplosion(): void {
    for (let i = 0; i < 30; i++) {
      const angle = (Math.PI * 2 * i) / 30;
      const speed = 100 + Math.random() * 150;
      this.particles.push(new Particle(
        this.position.x, this.position.y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed
      ));
    }
    this.shockwaves.push(new Shockwave(this.position.x, this.position.y));
  }
  
  private createSecondExplosion(): void {
    for (let i = 0; i < 50; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 150 + Math.random() * 200;
      this.particles.push(new Particle(
        this.position.x + (Math.random() - 0.5) * 40,
        this.position.y + (Math.random() - 0.5) * 40,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed
      ));
    }
    this.shockwaves.push(new Shockwave(this.position.x, this.position.y, 150));
  }
  
  private createFinalExplosion(): void {
    for (let i = 0; i < 100; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 200 + Math.random() * 300;
      const particle = new Particle(
        this.position.x, this.position.y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed
      );
      particle.size = 4 + Math.random() * 6;
      this.particles.push(particle);
    }
    this.shockwaves.push(new Shockwave(this.position.x, this.position.y, 200));
  }

  render(ctx: CanvasRenderingContext2D): void {
    const alpha = Math.max(0, 1 - (this.timer / this.maxTime) * 0.5);
    
    this.shockwaves.forEach(sw => sw.render(ctx));
    
    ctx.save();
    ctx.globalAlpha = alpha;
    this.particles.forEach(particle => particle.render(ctx));
    
    if (this.phase >= 2) {
      ctx.fillStyle = `rgba(255, 255, 255, ${(1 - this.timer / this.maxTime) * 0.8})`;
      ctx.beginPath();
      ctx.arc(this.position.x, this.position.y, 50 * (this.timer - 0.5), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

class Shockwave {
  position: Vector2;
  radius: number;
  maxRadius: number;
  speed: number = 300;
  
  constructor(x: number, y: number, speed: number = 300) {
    this.position = { x, y };
    this.radius = 0;
    this.maxRadius = 250;
    this.speed = speed;
  }
  
  update(deltaTime: number): void {
    this.radius += this.speed * deltaTime;
  }
  
  render(ctx: CanvasRenderingContext2D): void {
    const alpha = Math.max(0, 1 - (this.radius / this.maxRadius));
    ctx.save();
    ctx.globalAlpha = alpha * 0.5;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(this.position.x, this.position.y, this.radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
}

import { SimplePlayerExplosion } from './TestExplosion';

export class ExplosionManager {
  private explosions: Explosion[] = [];
  private playerDeathExplosion: PlayerDeathExplosion | null = null;
  private simplePlayerExplosion: SimplePlayerExplosion | null = null;

  update(deltaTime: number): void {
    this.explosions.forEach(explosion => explosion.update(deltaTime));
    this.explosions = this.explosions.filter(explosion => explosion.alive);
    
    if (this.playerDeathExplosion) {
      this.playerDeathExplosion.update(deltaTime);
      if (!this.playerDeathExplosion.alive) {
        this.playerDeathExplosion = null;
      }
    }
    
    if (this.simplePlayerExplosion) {
      this.simplePlayerExplosion.update(deltaTime);
      if (!this.simplePlayerExplosion.alive) {
        this.simplePlayerExplosion = null;
      }
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.explosions.forEach(explosion => explosion.render(ctx));
    if (this.playerDeathExplosion) {
      this.playerDeathExplosion.render(ctx);
    }
    if (this.simplePlayerExplosion) {
      this.simplePlayerExplosion.render(ctx);
    }
  }

  addExplosion(x: number, y: number): void {
    this.explosions.push(new Explosion(x, y));
  }

  addEnemyExplosion(x: number, y: number, enemyType: 'small' | 'medium' | 'large'): void {
    this.explosions.push(new EnemyExplosion(x, y, enemyType));
  }
  
  addPlayerDeathExplosion(x: number, y: number): void {
    this.simplePlayerExplosion = new SimplePlayerExplosion(x, y);
  }
  
  isPlayerDeathExplosionActive(): boolean {
    return this.simplePlayerExplosion !== null;
  }

  clear(): void {
    this.explosions = [];
    this.playerDeathExplosion = null;
    this.simplePlayerExplosion = null;
  }
}